import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import pluralize from "pluralize";
import {FieldErrors, FormProvider, useForm} from "react-hook-form";
import {toast} from "react-hot-toast";
import * as Yup from "yup";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {FileInputField} from "@/components/form/fields/FileInputField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {BulkImportResponse, GQL_BULK_IMPORT} from "@/lib/queries/checks.ts";
import {ResponseStatus} from "@/lib/types/common.ts";

interface FormValues {
  file: FileList;
}

const isValidFileType = (file: File): boolean => {
  const fileName = file.name.split(".").pop();
  return !!fileName && ["csv", "xls", "xlsx"].includes(fileName);
};

const FormSchema = Yup.object({
  file: Yup.mixed<FileList>()
    .required("Required")
    .test("fileLength", "Please select a file", (file) => file && file.length > 0)
    .test("fileExtension", "invalid file extension", (file) => file && file.length > 0 && isValidFileType(file[0])),
});

interface BulkImportFormProps {
  className?: string;
  formId: string;
  hide: () => void;
}

export const BulkImportForm = ({className, formId, hide}: BulkImportFormProps) => {
  const {brand} = useCurrentUserContext();
  const [bulkImport, {data}] = useMutation<BulkImportResponse>(GQL_BULK_IMPORT, {
    context: {
      noToast: true,
    },
  });
  const methods = useForm<FormValues>({
    resolver: yupResolver<FormValues>(FormSchema),
  });

  const {handleSubmit} = methods;

  const onSubmit = async ({file}: FormValues) => {
    const response = await bulkImport({
      variables: {
        file: file[0],
      },
    });

    if (response?.data?.response?.status === ResponseStatus.SUCCESS) {
      const amount = response?.data?.response?.data?.number;
      toast.success(`${amount} ${pluralize("Check", amount)} ${pluralize("has", amount)} been imported`);
      hide();
    }
  };

  const onError = (errors: FieldErrors) => {
    console.log(errors);
  };

  const resp = data?.response;
  const serverErrors = resp?.status === ResponseStatus.ERROR ? resp.message : "";

  return (
    <div className={className}>
      <FormProvider {...methods}>
        {serverErrors && <FormErrorAlerts className="mb-3" errors={serverErrors?.__all__} />}
        <form id={formId} onSubmit={handleSubmit(onSubmit, onError)}>
          <div className={"d-flex flex-column gap-3"}>
            <div className="pulseTextBody">
              Upload Checks in bulk into the system. Please download our{" "}
              <a download="" href={`${brand.siteURL}/static/devices/downloads/import_template.xltx`}>
                Bulk Upload Excel Template
              </a>
              , fill it out and submit it below.
            </div>
            <div className="row mb-3">
              <div className="col">
                <FileInputField
                  name={"file"}
                  label="Upload XLSX/CSV"
                  supportLinkText={"View sample file"}
                  supportLink={`${brand.siteURL}/static/devices/downloads/import_sample.xlsx`}
                />
              </div>
            </div>
            <div className="pulseTextBody">
              <div>
                A full guideline on all the fields can be found{" "}
                <a href={"https://support.uptime.com/hc/en-us/articles/360000719739-Bulk-Import-Tool-Use-and-Fields"}>
                  here
                </a>
                .
              </div>
              <div>Limited to importing 200 Checks at a time.</div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
